import React, { useMemo } from 'react';

import { Offer } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { useOfferTable } from '../../hooks/useOfferTable';
import { OfferPageLayout } from '../../components/OfferPageLayout';

import { useBundlesColumns } from './hooks/useBundlesColumns';

const BundlesTable = (): React.ReactElement => {
  const {
    getOffers,
    pricingsResult,
    isPricingsLoading,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    externalIdFilter,
    setExternalIdFilter,
    nameFilter,
    setNameFilter,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    processOffers
  } = useOfferTable({
    offerType: OfferType.BUNDLE,
    offerLabel: 'Bundle',
    needsPricings: true
  });

  const { columns } = useBundlesColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions,
    isPricingsLoading,
    pricingsResult
  });

  const offers: Offer[] = useMemo((): Offer[] => {
    if (getOffers.isLoading || !getOffers.data || !pricingsResult) {
      return [];
    }

    if (getOffers.data.offers) {
      return getOffers.data.offers.map((offer: Offer) => ({
        _id: offer.publisherOfferId,
        ...offer
      }));
    }

    if (getOffers.data.result) {
      return getOffers.data.result
        .filter((offer: Offer) => offer.type === 'Bundle')
        .map((offer: Offer) => ({
          _id: offer.publisherOfferId,
          ...offer
        }));
    }

    return [];
  }, [getOffers.isLoading, getOffers.data, pricingsResult]);

  const processed = useMemo(
    () => processOffers(offers),
    [offers, processOffers]
  );

  return (
    <OfferPageLayout
      title="Bundles"
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      offerLabel="Bundles"
      showFilters={offers.length > 0}
      externalIdFilter={externalIdFilter}
      onExternalIdFilterChange={setExternalIdFilter}
      nameFilter={nameFilter}
      onNameFilterChange={setNameFilter}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle="Add your first Bundle"
      emptyStateDescription="Bundles include one or more products sold at a discounted rate or with added value."
      createButtonPrefix="Bundle"
      bodyDataTestId="dashBundlesList"
      offerType={OfferType.BUNDLE}
      offerDesignType={OfferDesignTypes.Bundle}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle="Delete Bundle"
      deleteModalDescription="Are you sure you want to delete this bundle?"
    />
  );
};

export default BundlesTable;
